import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, Upload, Trash2, CheckCircle2, AlertCircle, 
  FileSignature, Calendar, Loader2, RefreshCw, X
} from 'lucide-react';
import { apiService } from '../../services/api';

// Constantes
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILE_SIZE_MB = 10;
const MESSAGE_AUTO_DISMISS_TIME = 5000; // 5 segundos

export default function TemplateManager() {
  // Estados
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [templateName, setTemplateName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);
  
  // 👇 Bandera para garantizar que la carga inicial solo ocurra una vez
  const hasFetchedRef = useRef(false);

  // Auto-dismiss para mensajes de error
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, MESSAGE_AUTO_DISMISS_TIME);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-dismiss para mensajes de éxito
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, MESSAGE_AUTO_DISMISS_TIME);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Cargar plantillas
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.request('/templates');
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar las plantillas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 👇 Solo ejecuta la carga si la bandera es falsa
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchTemplates();
    }
  }, [fetchTemplates]);

  // 👇 FUNCIÓN: Solo limpia los campos del formulario, NO los mensajes
  const clearFormData = useCallback(() => {
    setFileName('');
    setFileSize(0);
    setTemplateName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // 👇 FUNCIÓN COMPLETA: Limpia formulario Y mensajes (para usar después de éxito)
  const clearForm = useCallback(() => {
    clearFormData();
    setError('');
    setSuccess('');
  }, [clearFormData]);

  // Función para refrescar la lista manualmente
  const handleRefresh = useCallback(async () => {
    await fetchTemplates();
    setSuccess('Lista de plantillas actualizada');
    setTimeout(() => setSuccess(''), MESSAGE_AUTO_DISMISS_TIME);
  }, [fetchTemplates]);

  // Validar archivo seleccionado
  const handleFileSelect = useCallback((file) => {
    if (file) {
      // VALIDACIÓN DE TAMAÑO - Muestra error obligatorio
      if (file.size > MAX_FILE_SIZE) {
        setError(`El archivo "${file.name}" pesa ${(file.size / 1024 / 1024).toFixed(2)} MB y supera el límite de ${MAX_FILE_SIZE_MB} MB.`);
        clearFormData(); // ← Solo limpia los campos, NO el error
        return;
      }

      // VALIDACIÓN DE TIPO
      if (file.type !== 'application/pdf') {
        setError('Solo se permiten archivos en formato PDF.');
        clearFormData(); // ← Solo limpia los campos, NO el error
        return;
      }

      // Si todo está bien, limpia errores previos y guarda el archivo
      setError('');
      setFileName(file.name);
      setFileSize(file.size);
      
      // Auto-rellenar nombre si está vacío
      if (!templateName) {
        setTemplateName(file.name.replace(/\.pdf$/i, ''));
      }
    }
  }, [templateName, clearFormData]);

  // Subir plantilla
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!fileInputRef.current?.files[0] || !templateName.trim()) {
      setError('Por favor selecciona un archivo y asigna un nombre.');
      return;
    }

    if (fileSize > MAX_FILE_SIZE) {
      setError(`El archivo supera el límite de ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('template', fileInputRef.current.files[0]);
    formData.append('name', templateName.trim());

    try {
      const data = await apiService.upload('/templates/upload', formData);
      if (data.success) {
        setSuccess('Plantilla subida correctamente');
        clearForm(); // ← Aquí sí limpiamos todo (formulario + mensajes)
        await fetchTemplates();
      } else {
        setError(data.error || 'Error al subir la plantilla');
      }
    } catch (err) {
      if (err.status === 413) {
        setError(`El archivo es demasiado grande. El tamaño máximo permitido es ${MAX_FILE_SIZE_MB} MB.`);
      } else {
        setError(err.message || 'Error de conexión al subir la plantilla');
      }
    } finally {
      setUploading(false);
    }
  };

  // Eliminar plantilla
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta plantilla? Esta acción no se puede deshacer.')) {
      return;
    }

    setDeletingId(id);
    try {
      const data = await apiService.request(`/templates/${id}`, { method: 'DELETE' });
      if (data.success) {
        setSuccess('Plantilla eliminada correctamente');
        setTemplates(prev => prev.filter(t => t.id !== id));
        setTimeout(() => setSuccess(''), MESSAGE_AUTO_DISMISS_TIME);
      } else {
        setError(data.error || 'Error al eliminar');
      }
    } catch (err) {
      setError(err.message || 'Error de conexión al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Encabezado con botón de refrescar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Gestión de Plantillas</h2>
          <p className="text-slate-500 mt-1.5 text-sm">
            Sube y administra tus plantillas de contratos predefinidas para agilizar el proceso de firma.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          title="Actualizar lista de plantillas"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{loading ? 'Actualizando...' : 'Actualizar'}</span>
        </button>
      </div>

      {/* Mensajes de estado (auto-dismiss) */}
      {error && (
        <div className="relative p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">{error}</p>
            <p className="text-xs text-red-600 mt-1">
              Tamaño máximo permitido: <strong>{MAX_FILE_SIZE_MB} MB</strong> • Solo archivos PDF
            </p>
          </div>
          <button
            onClick={() => setError('')}
            className="p-1 hover:bg-red-100 rounded-lg transition-colors text-red-600"
            title="Cerrar mensaje"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="relative p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-emerald-700 font-medium">{success}</p>
          </div>
          <button
            onClick={() => setSuccess('')}
            className="p-1 hover:bg-emerald-100 rounded-lg transition-colors text-emerald-600"
            title="Cerrar mensaje"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario de subida */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Upload className="w-4 h-4 text-blue-600" />
                Nueva Plantilla
              </h3>
              {(fileName || templateName) && (
                <button
                  onClick={clearFormData}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Limpiar formulario"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <form onSubmit={handleUpload} className="space-y-4">
              {/* Nombre de la plantilla */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre de la Plantilla <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Ej. Contrato de Servicios"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>

              {/* Archivo PDF */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Archivo PDF <span className="text-red-500">*</span>
                  <span className="text-xs text-slate-400 font-normal ml-1">(Máx. {MAX_FILE_SIZE_MB} MB)</span>
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    group relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer 
                    transition-all duration-300 flex flex-col items-center justify-center min-h-[120px]
                    ${fileName 
                      ? (fileSize > MAX_FILE_SIZE ? 'border-red-400 bg-red-50/50' : 'border-blue-500 bg-blue-50/50') 
                      : 'border-slate-300 hover:border-blue-500 hover:bg-blue-50/50'
                    }
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />
                  {fileName ? (
                    <>
                      <FileText className={`w-8 h-8 mb-2 ${fileSize > MAX_FILE_SIZE ? 'text-red-600' : 'text-blue-600'}`} />
                      <p className="text-xs font-semibold text-slate-900 truncate max-w-[90%]">{fileName}</p>
                      <p className={`text-[10px] mt-1 ${fileSize > MAX_FILE_SIZE ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {(fileSize / 1024 / 1024).toFixed(2)} MB 
                        {fileSize > MAX_FILE_SIZE ? ' • Excede el límite' : ' • Clic para cambiar'}
                      </p>
                    </>
                  ) : (
                    <>
                      <FileSignature className="w-8 h-8 text-slate-400 group-hover:text-blue-600 mb-2 transition-colors" />
                      <p className="text-xs font-medium text-slate-600">Seleccionar PDF</p>
                      <p className="text-[10px] text-slate-400 mt-1">Máximo {MAX_FILE_SIZE_MB} MB</p>
                    </>
                  )}
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={uploading || !fileName || !templateName.trim() || fileSize > MAX_FILE_SIZE}
                  className="flex-1 bg-blue-600 text-white py-2.5 px-4 rounded-xl hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-600/10"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Guardar
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={clearFormData}
                  disabled={uploading || (!fileName && !templateName)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center justify-center gap-2"
                  title="Limpiar formulario"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">Limpiar</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Lista de plantillas */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Plantillas Activas</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                  {templates.length} {templates.length === 1 ? 'Plantilla' : 'Plantillas'}
                </span>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Actualizar lista"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {loading && templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-slate-300 animate-spin mb-3" />
                <p className="text-sm text-slate-500">Cargando plantillas...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-slate-300" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 mb-1">No hay plantillas aún</h4>
                <p className="text-xs text-slate-500 max-w-xs">
                  Sube tu primera plantilla usando el formulario de la izquierda.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {templates.map((template) => (
                  <div 
                    key={template.id} 
                    className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors group"
                  >
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-red-100">
                        <FileText className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 truncate" title={template.name}>
                          {template.name}
                        </h4>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                          <span className="text-xs text-slate-500 truncate max-w-[200px]" title={template.originalName}>
                            {template.originalName}
                          </span>
                          <span className="hidden sm:inline text-slate-300">•</span>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(template.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:justify-end flex-shrink-0">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                        Activo
                      </span>
                      
                      <button
                        onClick={() => handleDelete(template.id)}
                        disabled={deletingId === template.id}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Eliminar plantilla"
                      >
                        {deletingId === template.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}